import { useQuery } from "@tanstack/react-query";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import { DishIssueSource, DishIssueStatus } from "@fitness-app/data-models/entities/DishIssue";

import { usePermission } from "~/hooks/usePermission";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { type DishIssueListItem } from "~/modules/Nutrition/DishIssues/ingredientIssues";
import { supabase } from "~/store/initializeStore";

interface UseDishIssuesOptions {
  dishId?: string;
  ingredientId?: string;
  enabled?: boolean;
  ownerId?: string;
  onlyCount?: boolean;
}

export const useDishIssues = ({
  dishId,
  ingredientId,
  ownerId,
  enabled = true,
  onlyCount = false,
}: UseDishIssuesOptions) => {
  const { userId } = useUserRole();
  // Zgłoszenia spoza własnego katalogu widzi ten, kto prowadzi dietę szerzej
  // niż własny przydział. Pierwotnie stało tu `nutrition.ingredients.approve`
  // @ ALL — a ten klucz ma w katalogu wyłącznie ACCOUNT_OWNER, więc head coach
  // (baseline TEAM) stracił widok, który miał przed migracją (TRE-1855).
  // Samo zatwierdzanie składników dalej wisi na tamtym kluczu.
  const readsWholeCatalog = usePermission(PermissionKey.NUTRITION_MANAGE, PermissionScope.TEAM);
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ["dishIssues", dishId, ingredientId, ownerId, onlyCount, readsWholeCatalog, userId],
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const query = supabase
        .from("dish_issue")
        .select(
          onlyCount
            ? "id, createdAt, status"
            : "*,dish:dishId(*),ingredient:ingredientId(*),owner:ownerId(firstName,lastName,avatarUrl,email),reporter:reportedBy(firstName,lastName,avatarUrl,email),trainee:traineeId(id,firstName,lastName,email)",
          {
            count: "exact",
            head: onlyCount,
          },
        )
        .eq("status", DishIssueStatus.Reported)
        .order("createdAt", { ascending: false });

      if (dishId) {
        query.eq("dishId", dishId);
      }

      if (ingredientId) {
        query.eq("ingredientId", ingredientId);
      }

      // Client reports (TRE-2414) carry no `ownerId` - RLS already narrows them to the clients in the
      // user's `nutrition.manage` scope, so they join the trainer rows instead of being filtered by owner.
      if (ownerId) {
        if (readsWholeCatalog) {
          query.or(`ownerId.eq.${ownerId},ownerId.eq.${userId},ownerId.is.null,source.eq.${DishIssueSource.Trainee}`);
        } else {
          query.or(`ownerId.eq.${ownerId},source.eq.${DishIssueSource.Trainee}`);
        }
      }

      const { data, error, count } = await query.returns<DishIssueListItem[]>();

      if (error) {
        throw new Error(error.message);
      }

      return { list: data, count };
    },
    enabled,
  });

  return {
    issues: data?.list || [],
    count: data?.count || 0,
    isLoading,
    isFetching,
    error,
    refetch,
  };
};
